import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';
import { generateMaintenanceRequestNumber } from '../../utils/requestNumber';

export const createViolationsMaintenanceSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // ============ VIOLATIONS ============
  fetchViolations: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const raw = await apiService.getViolations(condoId);
      const violations = raw.map((v: any) => ({
        ...v,
        severity: v.severity?.toLowerCase(),
        status: v.status?.toLowerCase(),
      }));
      set({ violations, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch violations');
    } finally {
      setLoading(false);
    }
  },

  createViolation: async (violationData) => {
    const { setLoading, setError, fetchViolations } = get();
    setLoading(true);
    try {
      await apiService.createViolation(violationData);
      await fetchViolations(violationData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create violation');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateViolation: async (id, violationData) => {
    const { setLoading, setError, violations, fetchViolations } = get();
    setLoading(true);
    try {
      await apiService.updateViolation(id, violationData);
      const existing = violations.find(v => v.id === id);
      await fetchViolations(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update violation');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteViolation: async (id) => {
    const { setLoading, setError, violations, fetchViolations } = get();
    setLoading(true);
    try {
      const existing = violations.find(v => v.id === id);
      await apiService.deleteViolation(id);
      await fetchViolations(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete violation');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  sendViolationNotice: async (id) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.sendViolationNotice(id);
      set(state => ({
        violations: state.violations.map(v => v.id === id ? updated : v),
      }));
    } catch (error: any) {
      setError(error.message || 'Failed to send violation notice');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  resolveViolation: async (id) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.resolveViolation(id);
      set(state => ({
        violations: state.violations.map(v => v.id === id ? updated : v),
      }));
    } catch (error: any) {
      setError(error.message || 'Failed to resolve violation');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  addViolationNote: async (id, note) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.addViolationNote(id, note);
      set(state => ({
        violations: state.violations.map(v => v.id === id ? updated : v),
      }));
    } catch (error: any) {
      setError(error.message || 'Failed to add violation note');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ============ MAINTENANCE REQUESTS ============
  fetchMaintenanceRequests: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const raw = await apiService.getMaintenanceRequests(condoId);
      const maintenanceRequests = raw.map((r: any) => ({
        ...r,
        status: r.status?.toLowerCase(),
        priority: r.priority?.toLowerCase(),
      }));
      set({ maintenanceRequests, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch maintenance requests');
    } finally {
      setLoading(false);
    }
  },

  createMaintenanceRequest: async (reqData) => {
    const { setLoading, setError, fetchMaintenanceRequests } = get();
    setLoading(true);
    try {
      // Sprint 89A — backend MaintenanceRequestDTO requires UPPERCASE enum
      // constants (status ∈ SUBMITTED/IN_REVIEW/ASSIGNED/IN_PROGRESS/COMPLETED/
      // CANCELLED, priority ∈ LOW/MEDIUM/HIGH/URGENT) and a non-blank
      // requestNumber. The forms (AtriumOwnerHome, MyHomeHub) historically sent
      // lowercase labels and omitted requestNumber → HTTP 400. Normalize here
      // centrally so EVERY caller posts the correct contract regardless of what
      // it passes in. Idempotent: already-uppercase values and a supplied
      // requestNumber pass through unchanged.
      const payload: any = {
        ...reqData,
        requestNumber:
          (reqData as any).requestNumber && String((reqData as any).requestNumber).trim()
            ? (reqData as any).requestNumber
            // Sprint 147 (R14-24) — was `MR-${Date.now()}`, which produced
            // MR-1784513182486 next to the seeded/page convention MR-2026-2001.
            : generateMaintenanceRequestNumber(),
        status: ((reqData as any).status ?? 'submitted').toString().toUpperCase(),
        priority: ((reqData as any).priority ?? 'medium').toString().toUpperCase(),
      };
      await apiService.createMaintenanceRequest(payload);
      await fetchMaintenanceRequests(reqData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create maintenance request');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateMaintenanceRequest: async (id, reqData) => {
    const { setLoading, setError, maintenanceRequests, fetchMaintenanceRequests } = get();
    setLoading(true);
    try {
      await apiService.updateMaintenanceRequest(id, reqData);
      const existing = maintenanceRequests.find(r => r.id === id);
      await fetchMaintenanceRequests(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update maintenance request');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteMaintenanceRequest: async (id) => {
    const { setLoading, setError, maintenanceRequests, fetchMaintenanceRequests } = get();
    setLoading(true);
    try {
      const existing = maintenanceRequests.find(r => r.id === id);
      await apiService.deleteMaintenanceRequest(id);
      await fetchMaintenanceRequests(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete maintenance request');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateMaintenanceRequestStatus: async (id, status) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.updateMaintenanceRequestStatus(id, status);
      set(state => ({
        maintenanceRequests: state.maintenanceRequests.map(r => r.id === id ? updated : r),
      }));
    } catch (error: any) {
      setError(error.message || 'Failed to update maintenance request status');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ============ AMENITIES ============
  fetchAmenities: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      // Sprint 37 — finding H9. Round-5 saw "Swimming Pool / Rooftop
      // Pool / Fitness Center / Community Room / Guest Suite" on the
      // Maple amenities page even though Maple only seeds Party Room /
      // Gym / Rooftop Patio. Two failure modes were possible:
      //   (a) a no-condoId branch hitting `/condo/amenities` (the GET-all
      //       handler doesn't even exist on the backend → 404 leaks an
      //       old slice through), or
      //   (b) state carrying yesterday's amenities for another condo when
      //       the user switches buildings.
      // Belt-and-suspenders: clear first so the UI never paints a stale
      // cross-tenant list, then refuse the no-condoId call entirely.
      if (!condoId) {
        set({ amenities: [], error: null });
        return;
      }
      const amenities = await apiService.getAmenities(condoId);
      // Defensive filter — even if the API ever regresses, only keep
      // rows belonging to the active condo.
      const scoped = amenities.filter((a: any) => !a.condoId || a.condoId === condoId);
      set({ amenities: scoped, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch amenities');
    } finally {
      setLoading(false);
    }
  },

  createAmenity: async (amenityData) => {
    const { setLoading, setError, fetchAmenities } = get();
    setLoading(true);
    try {
      await apiService.createAmenity(amenityData);
      await fetchAmenities(amenityData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create amenity');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateAmenity: async (id, amenityData) => {
    const { setLoading, setError, amenities, fetchAmenities } = get();
    setLoading(true);
    try {
      await apiService.updateAmenity(id, amenityData);
      const existing = amenities.find(a => a.id === id);
      await fetchAmenities(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update amenity');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteAmenity: async (id) => {
    const { setLoading, setError, amenities, fetchAmenities } = get();
    setLoading(true);
    try {
      const existing = amenities.find(a => a.id === id);
      await apiService.deleteAmenity(id);
      await fetchAmenities(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete amenity');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ============ AMENITY BOOKINGS ============
  fetchAmenityBookings: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const raw = await apiService.getAmenityBookings(condoId);
      const amenityBookings = raw.map((b: any) => ({
        ...b,
        status: b.status?.toLowerCase(),
      }));
      set({ amenityBookings, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch amenity bookings');
    } finally {
      setLoading(false);
    }
  },

  createAmenityBooking: async (bookingData) => {
    const { setLoading, setError, fetchAmenityBookings } = get();
    setLoading(true);
    try {
      await apiService.createAmenityBooking(bookingData);
      await fetchAmenityBookings(bookingData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create amenity booking');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateAmenityBooking: async (id, bookingData) => {
    const { setLoading, setError, amenityBookings, fetchAmenityBookings } = get();
    setLoading(true);
    try {
      await apiService.updateAmenityBooking(id, bookingData);
      const existing = amenityBookings.find(b => b.id === id);
      await fetchAmenityBookings(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update amenity booking');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteAmenityBooking: async (id) => {
    const { setLoading, setError, amenityBookings, fetchAmenityBookings } = get();
    setLoading(true);
    try {
      const existing = amenityBookings.find(b => b.id === id);
      await apiService.deleteAmenityBooking(id);
      await fetchAmenityBookings(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete amenity booking');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  approveAmenityBooking: async (id) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.approveAmenityBooking(id);
      set(state => ({
        amenityBookings: state.amenityBookings.map(b => b.id === id ? updated : b),
      }));
    } catch (error: any) {
      setError(error.message || 'Failed to approve amenity booking');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  cancelAmenityBooking: async (id) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.cancelAmenityBooking(id);
      set(state => ({
        amenityBookings: state.amenityBookings.map(b => b.id === id ? updated : b),
      }));
    } catch (error: any) {
      setError(error.message || 'Failed to cancel amenity booking');
      throw error;
    } finally {
      setLoading(false);
    }
  },
});
